import ProTable, { ActionType, ProColumns } from "@ant-design/pro-table"
import React, { useEffect, useRef, useState } from "react"
import axiosApiInstance from "../../../../api/axiosClient"
import {
  AppleOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SpotifyOutlined,
  UserOutlined,
} from "@ant-design/icons"
import { Button, Form, Row, Space, Typography, Input, Spin } from "antd"
import {
  ModalForm,
  ProFormCheckbox,
  ProFormGroup,
  ProFormList,
  ProFormSelect,
  ProFormText,
} from "@ant-design/pro-form"
import { atom, useAtom, useAtomValue } from "jotai"
import { artistsAtom } from "../ddex_atoms"
import { queryClientAtom } from "jotai-tanstack-query"
import { singleArtistNameRules } from "../../../../common/singleArtistNameRules"
import { spotifyArtistIdRules } from "../../../../common/spotifyArtistId"
import EditArtistForm from "./EditArtistForm"
import DeleteArtistForm from "./DeleteArtistForm"

const isAddNewArtistModalOpen = atom(false)

const { Search } = Input

const NewArtist = () => {
  const [form] = Form.useForm<API.MusicDistribution.Artist>()
  const [isOpen, setIsOpen] = useAtom(isAddNewArtistModalOpen)
  const queryClient = useAtomValue(queryClientAtom)
  return (
    <ModalForm<API.MusicDistribution.Artist>
      title="Add new artist"
      width={1000}
      form={form}
      labelCol={{ span: 3 }}
      wrapperCol={{ span: 21 }}
      trigger={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ width: "100%" }}
          onClick={() => setIsOpen(true)}
        >
          Add
        </Button>
      }
      modalProps={{
        destroyOnClose: true,
        onCancel: () => setIsOpen(false),
      }}
      onFinish={async (values) => {
        await axiosApiInstance.post("/api/products/artists/", values)
        queryClient.invalidateQueries({ queryKey: ["artists"] })
        setIsOpen(false)
        return true
      }}
      open={isOpen}
    >
      <ProFormText
        label="Key Name"
        name="key_name"
        required
        rules={[
          {
            required: true,
          },
          ...singleArtistNameRules,
        ]}
      />
      <ProFormText
        name="spotify_id"
        label="Spotify ID"
        initialValue={undefined}
        tooltip="22-character Spotify artist ID, or paste an open.spotify.com / artists.spotify.com URL"
        rules={spotifyArtistIdRules}
      />
      <ProFormText
        name="apple_music_id"
        label="Apple ID"
        initialValue={undefined}
      />
      <ProFormList<API.MusicDistribution.Name>
        name="names"
        initialValue={[
          {
            full_name: "",
          },
        ]}
        min={1}
        // actionRef={actionRef}
        colProps={{ span: 24 }}
        creatorButtonProps={{
          position: "bottom",
          creatorButtonText: "New Name",
        }}
      >
        {(f, index, action) => (
          <ProFormGroup
            align="center"
            direction="horizontal"
            rowProps={{ justify: "start" }}
          >
            <ProFormText
              name="full_name"
              label="Full Name"
              placeholder={"Full Name"}
              width="md"
              rules={[
                {
                  required: true,
                },
                ...singleArtistNameRules,
              ]}
              required
            />
            <ProFormSelect
              name="full_name_language"
              label="Language"
              mode="single"
              width="xs"
              // options={countryOptions}
              options={[
                {
                  label: "🇬🇷",
                  value: "GR",
                },
                {
                  label: "🇬🇧",
                  value: "EN",
                },
              ]}
            />
            <ProFormCheckbox
              key={"is_default"}
              name={"is_default"}
              label="Default?"
              initialValue={index === 0}
            />
            <ProFormCheckbox
              key={"is_stage_name"}
              name={"is_stage_name"}
              label="Stage Name?"
            />
          </ProFormGroup>
        )}
      </ProFormList>
    </ModalForm>
  )
}

const ArtistsTable = () => {
  const queryClient = useAtomValue(queryClientAtom)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [deletingKey, setDeletingKey] = useState<string | null>(null)

  const { data: artists, status: statusArtists } = useAtomValue(artistsAtom)
  const [searchText, setSearchText] = useState("")
  const [filteredData, setFilteredData] = useState<
    API.MusicDistribution.Artist[]
  >([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (artists) {
      const filtered = artists.filter(
        (artist) =>
          artist.key_name.toLowerCase().includes(searchText.toLowerCase()) ||
          (artist.spotify_id &&
            artist.spotify_id
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (artist.apple_music_id &&
            artist.apple_music_id
              .toLowerCase()
              .includes(searchText.toLowerCase()))
      )
      setFilteredData(filtered)
      setLoading(false)
    }
  }, [searchText, artists])

  const columns: ProColumns<API.MusicDistribution.Artist>[] = [
    {
      title: "",
      key: "icon",
      search: false,
      hideInSearch: true,
      hideInForm: true,
      render: () => <UserOutlined />,
    },
    {
      title: "Artist",
      key: "Artist",
      dataIndex: "key_name",
      // search: true,
    },
    {
      title: (
        <Row align="middle">
          <SpotifyOutlined style={{ marginRight: "4px" }} />
          <Typography.Text>Spotify ID</Typography.Text>
        </Row>
      ),
      key: "Spotify",
      dataIndex: "spotify_id",
      search: false,
    },
    {
      title: (
        <Row align="middle">
          <AppleOutlined style={{ marginRight: "4px" }} />
          <Typography.Text>Apple ID</Typography.Text>
        </Row>
      ),

      key: "Apple",
      dataIndex: "apple_music_id",
      search: false,
    },
    {
      title: "Completed",
      key: "Completed",
      dataIndex: "is_completed",
      filterMode: "menu",
      render(_dom, entity) {
        if (entity.is_completed) {
          return (
            <CheckCircleOutlined
              style={{
                color: "green",
              }}
            />
          )
        }
        return (
          <CloseCircleOutlined
            style={{
              color: "red",
            }}
          />
        )
      },
    },
    {
      title: "",
      key: "actions",
      search: false,
      render(_dom, entity) {
        return (
          <>
            <Button
              type="link"
              htmlType="button"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setEditingKey(entity.key_name)
              }}
            />
            <Button
              type="link"
              htmlType="button"
              icon={<DeleteOutlined />}
              danger
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDeletingKey(entity.key_name)
              }}
            />
          </>
        )
      },
    },
  ]
  return (
    <>
      <Search
        placeholder="Search artists..."
        allowClear
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      <Spin spinning={loading}>
        <ProTable
          columns={columns}
          search={false}
          dataSource={filteredData}
          rowKey="key_name"
          // actionRef={actionRef}
          pagination={{ pageSize: 10 }}
          options={{
            reload: async () => {
              await queryClient.invalidateQueries({ queryKey: ["artists"] })
            },
          }}
          toolBarRender={() => [<NewArtist />]}
        />
      </Spin>
      {editingKey && (
        <EditArtistForm
          artistKey={editingKey}
          onClose={() => setEditingKey(null)}
        />
      )}
      {deletingKey && (
        <DeleteArtistForm
          artistKey={deletingKey}
          onClose={() => setDeletingKey(null)}
        />
      )}
    </>
  )
}

export default ArtistsTable
